import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Tab, Nav } from "react-bootstrap";
import { BackButton } from "../../assets/Icons";
import ChannelDetails from "../../pages/ChannelDetails";
import apiClient from "../../apiClient";
import { SSO_URL, UNO_APPLICATION_ID } from "../../env";
import {
  FETCH_APPLICATION_ENDPOINTS,
  FETCH_CHANNEL_APPLICATION_CONFIG,
  GET_CHANNEL_DETAILS,
} from "../../apiClient/apiEndpoints";
import LoginMethods from "../Tables/LoginMethods";
import ChannelApplications from "./ChannelApplications";
import SMTPConfig from "./SMTPConfig";
import GeneralConfig from "./GeneralConfig";
import EndpointVariable from "../Application/EndpointVariable";
import UpdateApplicationConfig from "../Application/UpdateApplicationConfig";
import { getValueType } from "../../utils/general";
import EditChannel from "./EditChannel";

const ChannelInfo = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [details, setDetails] = useState({});
  const [tabActiveKey, setTabActiveKey] = useState("application");
  const [unoEndPoints, setUnoEndPoints] = useState([]);
  const [unoConfigurationData, setUnoConfigurationData] = useState(null);
  const [navTabs, setNavTabs] = useState([]);
  const [editChannelModal, setEditChannelModal] = useState(false);

  const openEditChannelModal = () => setEditChannelModal(true);

  const handleChangeActiveKey = (selectedTab) => {
    setTabActiveKey(selectedTab);
  };

  const getChannelDetails = async () => {
    try {
      const data = await apiClient.get(`${SSO_URL}${GET_CHANNEL_DETAILS}/${id}`);
      setDetails(data);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchUnoEndPoints = async () => {
    try {
      const response = await apiClient.get(`${SSO_URL}${FETCH_APPLICATION_ENDPOINTS}/${id}/${UNO_APPLICATION_ID}`);
      setUnoEndPoints(response?.filter((e) => e.type === "FRONTEND_URL") || []);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchUnoConfiguration = async () => {
    try {
      const data = await apiClient.get(`${SSO_URL}${FETCH_CHANNEL_APPLICATION_CONFIG}/${id}/${UNO_APPLICATION_ID}`);
      setUnoConfigurationData(data);
    } catch (error) {
      console.log(error);
    }
  };

  const initializePage = async () => {
    await Promise.all([getChannelDetails(), fetchUnoConfiguration(), fetchUnoEndPoints()]);
  };

  useEffect(() => {
    initializePage();
  }, [id]);

  const refreshChannelData = () => {
    getChannelDetails();
    fetchUnoConfiguration();
  };

  useEffect(() => {
    if (unoConfigurationData) {
      const configToShow = unoConfigurationData?.defaultConfig?.filter(
        (item) => item.name !== "SMTP" && item.name !== "STORAGE" && item.name !== "ENV"
      );

      const dynamicTabs = configToShow?.map((item) => ({
        key: item.name,
        label: item.name,
        component: (
          <>
            {unoConfigurationData?.defaultConfig?.map((config) => {
              if (item.name === config.name) {
                const defaultConfig = config;
                let existingConfig = {};

                existingConfig = unoConfigurationData?.config?.find((e) => e.type === config.name) || {};

                const formattedConfigKeys = Object.entries(defaultConfig?.data || {}).map(([key, value]) => ({
                  key,
                  value,
                  dataType: getValueType(value),
                }));

                const mergedConfigVariables = formattedConfigKeys.map((configItem) => {
                  if (configItem.key in (existingConfig?.data || {})) {
                    return {
                      key: configItem.key,
                      value: existingConfig?.data[configItem.key],
                      dataType: configItem.dataType,
                    };
                  } else {
                    return configItem;
                  }
                });

                const mergedConfig = {
                  ...existingConfig,
                  data: mergedConfigVariables,
                };
                return (
                  <UpdateApplicationConfig
                    key={config.name}
                    applicationId={UNO_APPLICATION_ID}
                    defaultConfig={{ ...defaultConfig, data: formattedConfigKeys }}
                    configData={mergedConfig}
                    defaultConfigMode={false}
                    isEnv={false}
                  />
                );
              }
              return null;
            })}
          </>
        ),
      }));

      const baseTabs = [
        { key: "application", label: "Application Configuration", component: <ChannelApplications id={id} /> },
        { key: "smtp", label: "SMTP Configuration", component: <SMTPConfig id={id} /> },
        { key: "storage", label: "Storage Configuration", component: <GeneralConfig id={id} /> },
        { key: "loginMethods", label: "Login Methods", component: <LoginMethods id={id} /> },
        {
          key: "url",
          label: "Urls",
          component: (
            <EndpointVariable endpointURL={unoEndPoints} applicationIdProp={UNO_APPLICATION_ID} channelIdProp={id} />
          ),
        },
      ];

      setNavTabs([...baseTabs, ...dynamicTabs]);
    }
  }, [unoConfigurationData, unoEndPoints?.length]);

  return (
    <>
      <div className="container-fluid m-0 pt-2">
        <div className="col-12">
          <button className="backbtn cursor-pointer mt-2" onClick={() => navigate(-1)} title="Back">
            <BackButton />
            Back
          </button>
        </div>

        <ChannelDetails details={details} openEditChannelModal={openEditChannelModal} />

        <div className="col-12">
          <Tab.Container activeKey={tabActiveKey} onSelect={handleChangeActiveKey}>
            <div className="row mt-3">
              <div className="tabanddown">
                <Nav variant="pills" className="module-tab">
                  {navTabs.map((tab) => (
                    <Nav.Item key={tab.key}>
                      <Nav.Link eventKey={tab.key}>{tab.label}</Nav.Link>
                    </Nav.Item>
                  ))}
                </Nav>
              </div>
            </div>

            <Tab.Content className="mt-3 mb-4">
              {navTabs.map((tab) => (
                <Tab.Pane key={tab.key} eventKey={tab.key}>
                  {tab.component}
                </Tab.Pane>
              ))}
            </Tab.Content>
          </Tab.Container>
        </div>
      </div>
      <EditChannel
        editChannelModal={editChannelModal}
        setEditChannelModal={setEditChannelModal}
        channelData={{
          channel: details,
          configuration: unoConfigurationData?.config?.find((item) => item?.type === "GENERAL") || {},
        }}
        refresh={refreshChannelData}
      />
    </>
  );
};

export default ChannelInfo;
